import React, { useState, useRef, useEffect } from 'react';
import { GoogleMap, useLoadScript, Marker, Polyline, InfoWindow } from '@react-google-maps/api';
import { GOOGLE_MAPS_API } from '../../../environment_variable';
import { Button, Typography, Divider, Box, Paper, FormControl, Checkbox, FormControlLabel, Card, CardContent, Collapse } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';


import { object_category_street_surface } from './Utilities';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const libraries = ['places'];
const center = {
  lat: 47.24466348808146,
  lng: -122.43676839276614,
};

// Data structure for street surface infrastructure
// count: Reflects the number of this sub-asset type in the search area.
// averageWidth: Average lane width for lanes and similar features.
// segments: Detailed geolocation data for each individual feature (start and end points).
// info: Provides additional context, such as width, purpose, or height.
const streetMarkingsData = {
  "Lane Markings": {
    color: "#bbe3f2",
    items: [
      {
        id: 1,
        name: "Solid White Line",
        count: 15, // Average count of solid white lines in search area
        averageWidth: "3.2m", // Average lane width
        color: "#788bf5", // Softer Blue
        segments: [
          {
            id: "SWL-1",
            path: [
              { lat: 47.2529, lng: -122.4411 },
              { lat: 47.2535, lng: -122.4428 },
            ],
            info: "Lane Marking: Solid White Line, Lane Width: 3.5m",
          },
          {
            id: "SWL-2",
            path: [
              { lat: 47.2542, lng: -122.4443 },
              { lat: 47.2550, lng: -122.4458 },
            ],
            info: "Lane Marking: Solid White Line, Lane Width: 3.0m",
          },
        ],
      },
      {
        id: 2,
        name: "Dashed White Line",
        color: "#1A73E8", // Deep Sky Blue
        count: 10,
        averageWidth: "3.4m",
        segments: [
          {
            id: "DWL-1",
            path: [
              { lat: 47.2561, lng: -122.4472 },
              { lat: 47.2570, lng: -122.4485 },
            ],
            info: "Lane Marking: Dashed White Line, Lane Width: 3.5m",
          },
        ],
      },
      {
        id: 3,
        name: "Solid Yellow Line",
        count: 5,
        color: "#64B5F6", // Light Aqua Blue
        averageWidth: "3.4m",
        segments: [
          {
            id: "DWL-1",
            path: [
              { lat: 47.2561, lng: -122.4472 },
              { lat: 47.2570, lng: -122.4485 },
            ],
            info: "Lane Marking: Solid Yellow Line, Lane Width: 3.5m",
          },
        ],
      },
      {
        id: 4,
        name: "Dashed Yellow Line",
        color: "#0D47A1", // Navy Deep Blue
        count: 8,
        averageWidth: "3.4m",
        segments: [
          {
            id: "DWL-1",
            path: [
              { lat: 47.2561, lng: -122.4472 },
              { lat: 47.2570, lng: -122.4485 },
            ],
            info: "Lane Marking: Dashed Yellow Line, Lane Width: 3.5m",
          },
        ],
      },
    ],
  },
  "Crossings and Crosswalks": {
    color: "#ffaaa6",
    items: [
      {
        id: 2,
        name: "Pedestrian Crosswalks (Zebra, Ladder)",
        count: 5,
        color: "#FFCDD2", // Pale Pink
        segments: [
          {
            id: "PC-1",
            path: [
              { lat: 47.2582, lng: -122.4499 },
              { lat: 47.2595, lng: -122.4505 },
            ],
            info: "Pedestrian Crosswalk: Zebra, Width: 4.5m",
          },
        ],
      },
      {
        id: 3,
        name: "School Zone Crosswalks",
        count: 7,
        color: "#F06292", // Bubblegum Pink
        segments: [
          {
            id: "PC-2",
            path: [
              { lat: 47.2582, lng: -122.4499 },
              { lat: 47.2595, lng: -122.4505 },
            ],
            info: "Pedestrian Crosswalk: Zebra, Width: 4.5m",
          },
        ],
      },
    ],
  },
  "Speed Control Features": {
    color: "#eef2b6",
    items: [
      {
        id: 1,
        name: "Speed Bumps",
        count: 5,
        color: "#FFE082", // Sun Yellow
        segments: [
          {
            id: "SB-1",
            path: [
              { lat: 47.2601, lng: -122.4522 },
              { lat: 47.2610, lng: -122.4535 },
            ],
            info: "Speed Bump: Height 0.1m, Length: 3.0m",
          },
        ],
      },
      {
        id: 2,
        name: "Speed Humps",
        count: 10,
        color: "#FFCA28", // Mustard Yellow
        segments: [
          {
            id: "SB-1",
            path: [
              { lat: 47.2601, lng: -122.4522 },
              { lat: 47.2610, lng: -122.4535 },
            ],
            info: "Speed Hump: Height 0.1m, Length: 3.0m",
          },
        ],
      },
      {
        id: 3,
        name: "Raised Intersections",
        color: "#FFA000", // Burnt Orange
        count: 8,
        segments: [
          {
            id: "SB-1",
            path: [
              { lat: 47.2601, lng: -122.4522 },
              { lat: 47.2610, lng: -122.4535 },
            ],
            info: "Raised Intersection: Height 0.1m, Length: 3.0m",
          },
        ],
      },
    ],
  },
  "Traffic Guidance Markings": {
    color: "#b2f7ca",
    items: [
      {
        id: 1,
        name: "Turn Lanes (Left, Right, U-Turn)",
        count: 20,
        color: "#A5D6A7", // Mint Green
        averageWidth: "3.3m",
        segments: [
          {
            id: "TL-1",
            path: [
              { lat: 47.2623, lng: -122.4549 },
              { lat: 47.2635, lng: -122.4562 },
            ],
            info: "Turn Lane: Left, Lane Width: 3.3m",
          },
          {
            id: "TR-1",
            path: [
              { lat: 47.2623, lng: -122.4549 },
              { lat: 47.2635, lng: -122.4562 },
            ],
            info: "Turn Lane: Right, Lane Width: 3.3m",
          },
          {
            id: "TU-1",
            path: [
              { lat: 47.2623, lng: -122.4549 },
              { lat: 47.2635, lng: -122.4562 },
            ],
            info: "Turn Lane: U-Turn, Lane Width: 3.3m",
          },
        ],
      },
      {
        id: 2,
        name: "Shared Lanes (Centerline, Bike Lanes)",
        count: 10,
        color: "#66BB6A", // Fresh Grass Green
        averageWidth: "3.3m",
        segments: [
          {
            id: "TL-1",
            path: [
              { lat: 47.2623, lng: -122.4549 },
              { lat: 47.2635, lng: -122.4562 },
            ],
            info: "Shared Lanes, Lane Width: 3.3m",
          }
        ],
      },
      {
        id: 3,
        name: "Merge Zones",
        count: 8,
        color: "#388E3C", // Jungle Green
        averageWidth: "3.3m",
        segments: [
          {
            id: "TL-1",
            path: [
              { lat: 47.2623, lng: -122.4549 },
              { lat: 47.2635, lng: -122.4562 },
            ],
            info: "Merge Zones, Lane Width: 3.3m",
          }
        ],
      },
    ],
  },
  "Pedestrian and Cyclist Safety Features": {
    color: "#ecd3f5",
    items: [
      {
        id: 1,
        name: "Pedestrian Islands",
        count: 12,
        color: "#CE93D8", // Orchid
        averageWidth: "2.5m",
        segments: [
          {
            id: "PBL-1",
            path: [
              { lat: 47.2648, lng: -122.4575 },
              { lat: 47.2652, lng: -122.4589 },
            ],
            info: "Protected Bike Lane, Width: 2.5m",
          },
        ],
      },
      {
        id: 2,
        name: "Painted Bike Boxes",
        count: 20,
        color: "#6A1B9A", // Plum
        averageWidth: "2.5m",
        segments: [
          {
            id: "PBL-1",
            path: [
              { lat: 47.2648, lng: -122.4575 },
              { lat: 47.2652, lng: -122.4589 },
            ],
            info: "Protected Bike Lane, Width: 2.5m",
          },
        ],
      },
      {
        id: 3,
        name: "Protected Bike Lanes",
        count: 12,
        color: "#5c0848", // Plum
        averageWidth: "2.5m",
        segments: [
          {
            id: "PBL-1",
            path: [
              { lat: 47.2648, lng: -122.4575 },
              { lat: 47.2652, lng: -122.4589 },
            ],
            info: "Protected Bike Lane, Width: 2.5m",
          },
        ],
      },
    ],
  },
  "Other Surface Markings": {
    color: "#fadcb4",
    items: [
      {
        id: 7,
        name: "Bus-Only Lanes",
        count: 3,
        color: '#fa9c1e',
        averageWidth: "3.8m",
        segments: [
          {
            id: "BOL-1",
            path: [
              { lat: 47.2661, lng: -122.4600 },
              { lat: 47.2670, lng: -122.4615 },
            ],
            info: "Bus-Only Lane, Width: 3.8m",
          },
        ],
      },
    ],
  },
};



const StreetSurface = () => {
  const [map, setMap] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [visibleAssets, setVisibleAssets] = useState({});
  const [selectedPolyline, setSelectedPolyline] = useState(null);

  const [showLayerOptions, setShowLayerOptions] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [address, setAddress] = useState('');
  const [searchResults, setSearchResults] = useState({
    totalCurbs: 12,
    totalBikeLanes: 8,
    totalLanes: 24,
    speedBumps: 5,
    crosswalks: 6,
    mainRoadLanes: 10,
    residentialLanes: 8,
    businessLanes: 6,
  });

  const [layersVisibility, setLayersVisibility] = useState({
    trafficFeatures: true,
    roadwaySegments: true,
    streetSurfaces: true,
  });

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API,
    libraries,
  });

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  const handleExpandClick = (index) => {
    setExpanded(expanded === index ? null : index);
  };

  const toggleAssetVisibility = (id) => {
    setVisibleAssets((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLayerToggle = (layer) => {
    setLayersVisibility((prevState) => ({
      ...prevState,
      [layer]: !prevState[layer],
    }));
  };

  const handleSearch = () => {
    setSearchResults({
      totalCurbs: 15,
      totalBikeLanes: 10,
      totalLanes: 30,
      speedBumps: 8,
      crosswalks: 9,
      mainRoadLanes: 12,
      residentialLanes: 10,
      businessLanes: 8,
    });
  };

  const chartData = (category) => ({
    labels: streetMarkingsData[category].items.map((item) => item.name),
    datasets: [
      {
        label: 'Number of Assets',
        data: streetMarkingsData[category].items.map((item) => item.count),
        backgroundColor: streetMarkingsData[category].items.map((item) => item.color), // Use sub-asset colors
        borderColor: streetMarkingsData[category].items.map((item) => item.color),
        borderWidth: 1,
      },
    ],
  });
  
  const getMarkerColor = (hexColor) => {
    // Simplified example: map hex colors to Google Maps marker colors
    const colorMap = {
      '#64B5F6': 'blue',
      '#1E88E5': 'blue',
      '#1976D2': 'blue',
      '#42A5F5': 'blue',
      '#1565C0': 'blue',
      '#EF5350': 'red',
      '#E53935': 'red',
      '#D32F2F': 'red',
      '#B71C1C': 'red',
      '#F44336': 'red',
      '#FFB74D': 'orange',
      '#FF9800': 'orange',
      '#FB8C00': 'orange',
      '#81C784': 'green',
      '#4CAF50': 'green',
      '#388E3C': 'green',
      '#BA68C8': 'purple',
      '#8E24AA': 'purple',
    };
    return colorMap[hexColor] || 'yellow'; // Default to yellow if no match
  };

  const mapContainerStyle = {
    width: 'calc(100vw - 300px)',
    height: '89vh',
    position: 'relative',
  };

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'Roboto, sans-serif' }}>
      {/* Left Panel */}
      <Paper elevation={3} style={{ width: '350px', overflowY: 'auto', padding: '10px', boxShadow: '2px 0px 5px rgba(0,0,0,0.1)' }}>

        <Typography variant="h6" fontWeight="600" gutterBottom>
          Search Criteria
        </Typography>
        <Divider />
        <Box mt={2}>
          <FormControl fullWidth margin="normal">
            <Typography variant="body2" gutterBottom>
              Address
            </Typography>
            <input
              type="text"
              placeholder="Enter address"
              className="w-full p-2 border rounded"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              style={{ border: '1px solid #ccc', borderRadius: '5px', padding: '8px' }}
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <Typography variant="body2" gutterBottom>
              Search Range (in miles)
            </Typography>
            <input
              type="number"
              placeholder="Enter range"
              className="w-full p-2 border rounded"
              style={{ border: '1px solid #ccc', borderRadius: '5px', padding: '8px' }}
            />
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            style={{ marginTop: '20px' }}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Box>

        <Divider />


            <Box sx={{ padding: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Search Results
          </Typography>

          {/* {Object.keys(data).map((category, index) => {
            const chartData = {
              labels: data[category].items.map((item) => item.name),
              datasets: [
                {
                  label: 'Number of Assets',
                  data: data[category].items.map((item) => item.count),
                  backgroundColor: data[category].items.map((item) => item.color),
                  borderColor: data[category].items.map((item) => item.color),
                  borderWidth: 1,
                },
              ],
            };

            return (
              <Card key={index} elevation={3} style={{ marginBottom: '10px' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1">{category}</Typography>
                    <Button onClick={() => handleExpandClick(index)}>
                      {expanded === index ? 'Collapse' : 'Expand'}
                    </Button>
                  </Box>
                </CardContent>
                <Collapse in={expanded === index} timeout="auto" unmountOnExit>
                  <Divider />
                  <CardContent>
                    <Box sx={{ height: '300px' }}>
                      <Bar data={chartData} options={{ responsive: true, maintainAspectRatio: false }} />
                    </Box>
                    <Box mt={2}>
                      {data[category].items.map((item) => (
                        <Box key={item.id} display="flex" alignItems="center">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={visibleAssets[item.id] !== false}
                                onChange={() => toggleAssetVisibility(item.id)}
                                style={{ color: item.color }}
                              />
                            }
                            label={<Typography variant="body2" style={{ color: item.color }}>{item.name}</Typography>}
                          />
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Collapse>
              </Card>
            );
          })} */}

{Object.keys(streetMarkingsData).map((category, index) => {
  return (
    <Card key={index} elevation={3} sx={{ borderRadius: 2, backgroundColor: streetMarkingsData[category].color }}>
      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold" fontSize="18px">
          {category}
        </Typography>
        <Button
          onClick={() => handleExpandClick(index)}
          endIcon={
            <ExpandMore
              sx={{
                transform: expanded === index ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            />
          }
        >
          {expanded === index ? 'Collapse' : 'Expand'}
        </Button>
      </CardContent>
      <Collapse in={expanded === index} timeout="auto" unmountOnExit>
        <Divider />
        <CardContent>
          {/* Bar Chart */}
          <Box sx={{ height: '300px', mt: 2 }}>
            <Bar
              data={{
                labels: streetMarkingsData[category].items.map((item) => item.name),
                datasets: [
                  {
                    label: 'Count',
                    data: streetMarkingsData[category].items.map((item) => item.count || 1),
                    backgroundColor: streetMarkingsData[category].items.map((item) => item.color),
                  },
                ],
              }}
              options={{ responsive: true, maintainAspectRatio: false }}
            />
          </Box>

          {/* Legend */}
          <Box sx={{ mt: 2, mb: 3, p: 2, border: '1px solid #ccc', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Legend
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Click to show/hide markers or polylines
            </Typography>
            {streetMarkingsData[category].items.map((item) => {
              const isDarkBackground = (color) => {
                if (!color || typeof color !== 'string' || !color.startsWith('#')) {
                  console.error('Invalid color value:', color);
                  return false; // Default to light background if invalid
                }
              
                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                const brightness = (r * 299 + g * 587 + b * 114) / 1000;
              
                return brightness < 128; // Consider it dark if brightness is below 128
              };

              
    

              const textColor = isDarkBackground(item.color) ? '#FFFFFF' : '#000000';
              const checkboxStyle = {
                color: textColor,
                '&.Mui-checked': {
                  color: textColor,
                },
              };

              return (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1,
                    padding: '5px',
                    borderRadius: '4px',
                    backgroundColor: item.color,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visibleAssets[item.id] !== false}
                        onChange={() =>
                          setVisibleAssets((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                        }
                        sx={checkboxStyle}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          color: textColor,
                        }}
                      >
                        {item.name}
                      </Typography>
                    }
                  />
                </Box>
              );
            })}
          </Box>

          {/* Detailed Info */}
          {streetMarkingsData[category].items.map((item) => (
            <Box key={item.id} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {item.name} - Details
              </Typography>
              <ul>
                {item.segments.map((segment) => (
                  <li key={segment.id}>
                    <Typography variant="body2">
                      {segment.info} (ID: {segment.id})
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          ))}
        </CardContent>
      </Collapse>
    </Card>
  );
})}




        </Box>
      </Paper>

      {/* Main Map */}
      <div style={{ flexGrow: 1, position: 'relative' }}>


      <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={15}
          onLoad={(mapInstance) => setMap(mapInstance)}
          mapTypeId="satellite" // Set the map type to satellite
        >
        {/* Render Polylines */}
        {Object.keys(streetMarkingsData).map((category) =>
          streetMarkingsData[category].items.map((item) =>
            item.type === "polyline" && visibleAssets[item.id] !== false ? (
              <Polyline
                key={item.id}
                path={item.path}
                options={{
                  strokeColor: item.color,
                  strokeOpacity: 1,
                  strokeWeight: 4,
                }}
                onClick={() => setSelectedPolyline(item)}
              />
            ) : null
          )
        )}
      </GoogleMap>

      </div>
    </div>
  );
};

export default StreetSurface;
